import React, {useEffect, useRef} from 'react'
import {Controller} from 'react-hook-form'
import {convertSelectedValueType, dataToSelectOptions} from '../../utils/common'
import {ReachSelectTypes} from './ReachSelectTypes'
import Select from 'react-select'
import './reachSelect.scss'

function ReachSelect({
  id,
  name,
  value,
  control,
  label,
  className = '',
  selectClass = '',
  onChange,
  disabled = false,
  isRequired,
  options,
  formLabel,
  error,
  touched,
  placeholder,
  isNullable,
  defaultValue = '',
  selectRef,
  registerKey,
  onBlur,
  placeholderValue = '',
  isReadOnly = false,
  labelClass = '',
  labelKey = 'label',
  valueKey = 'value',
  valueType = 'string',
  isLoading = false,
  registerOptions,
  disabledKey,
  isFloatingMenu = true,
  disableDropdownIndicator = false,
  menuStyle,
  autoFocus,
  passAllOptionData,
  searchOptionsBy = ['label'],
  description,
  descriptionClass = '',
  ...rest
}: ReachSelectTypes) {
  const internalSelectRef = useRef<any>(null)
  const formattedOptions =
    options && options?.length > 0
      ? labelKey && valueKey
        ? dataToSelectOptions(
            options,
            labelKey,
            valueKey,
            valueType,
            disabledKey,
            'isDisabled',
            passAllOptionData
          )
        : dataToSelectOptions(
            options,
            'label',
            'value',
            valueType,
            disabledKey,
            'isDisabled',
            passAllOptionData
          )
      : []

  const handleChange = (selectedOption: any, registerOnChange?: any) => {
    const selectedValue = selectedOption
      ? convertSelectedValueType(options, selectedOption.value)
      : placeholderValue
    if (onChange)
      onChange(
        selectedOption ? selectedOption : {label: placeholderValue, value: placeholderValue},
        selectedValue
      )
    if (registerOnChange && registerKey) registerOnChange(selectedValue)
  }

  function mergeRefs<T>(...refs: React.Ref<T>[]) {
    return (instance: T | null) => {
      refs.forEach((ref) => {
        if (typeof ref === 'function') {
          ref(instance)
        } else if (ref && typeof ref === 'object') {
          ;(ref as React.MutableRefObject<T | null>).current = instance
        }
      })
    }
  }

  const filterOption = (option: any, inputValue: string) => {
    if (option.value === 'searching') return true
    const searchValues = searchOptionsBy.map((key) =>
      option?.data?.[key]?.toString()?.toLowerCase()
    )
    return searchValues.some((value) => {
      return value?.includes(inputValue.toLowerCase())
    })
  }

  useEffect(() => {
    if (autoFocus && internalSelectRef.current) {
      requestAnimationFrame(() => {
        internalSelectRef.current?.focus()
      })
    }
  }, [autoFocus])

  return (
    <div className={'position-relative ' + className}>
      {label && (
        <label
          className={`form-label ${labelClass} ${isRequired ? 'required' : ''}`}
          form-label={formLabel}
        >
          {label}
        </label>
      )}

      {control && registerKey ? (
        <Controller
          control={control}
          name={registerKey}
          defaultValue={defaultValue}
          rules={registerOptions}
          render={({field: {value: registerValue, onChange: registerOnChange}}) => {
            const defaultRegisteredValue = formattedOptions.find(
              (option: any) => option.value === registerValue
            )
            return (
              <Select
                options={formattedOptions}
                placeholder={placeholder}
                isLoading={isLoading}
                className={`${selectClass} ${
                  error ? 'is-invalid border border-1 border-danger' : ''
                }`}
                id={id}
                name={name}
                ref={mergeRefs(internalSelectRef, selectRef)}
                onChange={(selectedOption: any) => {
                  handleChange(selectedOption, registerOnChange)
                }}
                onBlur={onBlur}
                {...(value ? {value: value} : {value: defaultRegisteredValue ?? placeholderValue})}
                defaultValue={defaultRegisteredValue}
                isDisabled={disabled || isReadOnly}
                isClearable={isNullable}
                classNamePrefix='react-select'
                filterOption={filterOption}
                styles={{
                  menu: (provided: any) => ({
                    ...provided,
                    ...(!isFloatingMenu && {position: 'relative'}),
                    ...(!isFloatingMenu && {boxShadow: 'none !important'}),
                    ...(!isFloatingMenu && {border: 'none !important'}),
                    ...menuStyle,
                  }),
                  dropdownIndicator: (provided: any) => ({
                    ...provided,
                    display: disableDropdownIndicator ? 'none' : 'block',
                  }),
                }}
                {...rest}
              />
            )
          }}
        />
      ) : (
        <Select
          options={formattedOptions}
          placeholder={placeholder}
          isLoading={isLoading}
          className={`${selectClass} ${error ? 'is-invalid border border-1 border-danger' : ''}`}
          id={id}
          name={name}
          ref={mergeRefs(internalSelectRef, selectRef)}
          onChange={handleChange}
          onBlur={onBlur}
          {...(value ? {value: value} : {})}
          defaultValue={defaultValue}
          isDisabled={disabled || isReadOnly}
          isClearable={isNullable}
          classNamePrefix='react-select'
          filterOption={filterOption}
          styles={{
            menu: (provided: any) => ({
              ...provided,
              ...(!isFloatingMenu && {position: 'relative'}),
              ...(!isFloatingMenu && {boxShadow: 'none !important'}),
              ...(!isFloatingMenu && {border: 'none !important'}),
              ...menuStyle,
            }),
            dropdownIndicator: (provided: any) => ({
              ...provided,
              display: disableDropdownIndicator ? 'none' : 'block',
            }),
          }}
          {...rest}
        />
      )}
      {description && (
        <div className={`form-text text-muted mt-2 ${descriptionClass}`}>
          {description}
        </div>
      )}
      {error && error?.message && (
        <div className='form-error invalid-feedback'>{error.message}</div>
      )}
    </div>
  )
}

export default ReachSelect
